import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { performSearch } from '../search/thunks/search.thunk';
import { selectResults } from '../search/store/search.selector';
import { SearchResult } from '../search/interfaces/search.interface';
import '../search/search-results';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { booleanConverter } from '../../common/converters';
import { appendSchemaToHead } from '../../common/utils/seo-schema-utils';
import './product-cards-pagination';
import { getProductUrl } from './utils/product-card.util';
import { mapProductCardData } from '../search/mapping/search-product-card.mapping';
import "./variations/k12-product-card.display";
import { CtaTypeLabel } from './product.interface';
import ProductCardSearchUtil from "./utils/product-card.search.util";

enum ComponentType {
  shared = 'shared',
  isolated = 'isolated',
}

@customElement('mhecomm-k12-product-cards')
export class ProductCards extends connect(store)(BaseComponent) {
  @property()
  cardType?: string;

  @property()
  businessUnit?: string;

  @property()
  k12ProductIdentifierType?: string;

  @property({ converter: booleanConverter })
  limitCard?: boolean;

  @property({ converter: booleanConverter })
  lazyLoadImage?: boolean = true;

  @property({ type: Number })
  numberofcards?: number;

  @property()
  orientation = 'vertical';

  @property()
  cta = CtaTypeLabel.NO_CTA;

  @property({ converter: booleanConverter })
  showCopyright = false;

  @property({ converter: booleanConverter })
  showEdition = false;

  @property({ converter: booleanConverter })
  showAuthors = false;

  @property({ converter: booleanConverter })
  showISBN = false;

  @property({ converter: booleanConverter })
  showProgramFamily = false;

  @property({ converter: booleanConverter })
  showGrades = false;

  @property({ converter: booleanConverter })
  showBusinessUnitLabel = false;

  @property({ converter: booleanConverter })
  showProductType = false;

  @property({ converter: booleanConverter })
  isEditorMode?: boolean;

  @property({ type: Array })
  identifiers?: Array<string>;

  @property()
  programCodeForProducts?: string;

  @property()
  authoredId?: string;

  @property()
  componentType?: ComponentType = ComponentType.isolated;

  @property({ type: String })
  headingLevel = "h3";

  pageSize = 10;

  @state()
  private results: Array<SearchResult> = [];

  @state()
  private start = 1;

  @state()
  private total = 0;

  private programIdList;
  private searchFilter;
  private isbnList;
  private requireProgramData;
  private programData;

  @property()
  sorting?: string;

  connectedCallback() {
    super.connectedCallback();
    if (this.cardType === 'k12Product') {
      if (this.programCodeForProducts && this.showProgramFamily) {
        this.requireProgramData = true;
      }
    }
    this._search(this.start);
  }

  private async _search(page: number) {
    let response: any;
    if (this.identifiers || this.programCodeForProducts) {
      if (this.cardType === 'k12Program') {
        this.programIdList = this.identifiers;
      } else if (this.cardType === 'k12Product' || this.cardType === 'heProduct') {
        if (this.programCodeForProducts) {
          this.searchFilter = {
            type: 'FieldFilter',
            paramName: 'PROGRAM_CODE',
            value: this.programCodeForProducts,
            conditionalOperator: 'EQ',
          };
        } else {
          this.isbnList = this.identifiers;
        }
      }
      const searchParameters = {
        programIdList: this.programIdList,
        isbnList: this.isbnList,
        catalog: this.determineCatalog(),
        start: page,
        facets: false,
        pageLength: this.pageSize,
        filter: this.searchFilter,
        requireProgramData: this.requireProgramData
      };
      if (ProductCardSearchUtil.isSortAlphabetically(this.sorting)) {
        searchParameters['sortBy'] = "title_asc";
      }
      response = await store.dispatch(
        performSearch(searchParameters,false)
      );
    }
    if (this.componentType === ComponentType.isolated) {
      this.results = response?.hits || [];
      if (this.requireProgramData && this.showProgramFamily) {
        this.programData = response.programData;
      }
    }
  }

  determineCatalog(): string | undefined {
    if (
      this.businessUnit === 'K12' &&
      this.cardType === 'k12Product' &&
      this.k12ProductIdentifierType === 'k12ProductByIsbn'
    ) {
      return 'SEG_PROGRAM';
    } else if (
      this.businessUnit === 'K12' &&
      this.cardType === 'k12Product' &&
      this.k12ProductIdentifierType === 'k12ProductByProgramId'
    ) {
      return 'SEG_PRODUCT';
    }
    return;
  }

  stateChanged(state: StoreState) {
    if (this.componentType === ComponentType.shared) {
      this.results = selectResults(state) || [];
    }
  }

  getSchema(results: SearchResult[]) {
    return {
      '@context': 'http://schema.org',
      '@type': 'ItemList',
      numberOfItems: this.total,
      itemListElement: results.map((result, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'ProductCollection',
            image: this.getCardImageUrl(result.document),
            name: result.document.title,
            description: result.document.description,
            url: getProductUrl(result.document),
          },
        };
      }),
    };
  }

  getCardImageUrl(product) {
    switch (product.type) {
      case 'SEGProductDocument':
      case 'SEGProgramDocument':
        switch (product.documentType) {
          case 'Products':
          default:
            return getImageSrcUrl(product?.isbn13 || product?.isbn);
        }
    }
    return '';
  }

  renderComp() {
    const limitedResults =
      this.limitCard && this.numberofcards ? this.results.slice(0, this.numberofcards) : this.results;
    appendSchemaToHead(this.getSchema(limitedResults), this.authoredId || '');
    const productCardsList = mapProductCardData(limitedResults, 'SEG_PROGRAM');
    if (ProductCardSearchUtil.isSortManually(this.sorting)) {
      ProductCardSearchUtil.sortResultsByManualType(productCardsList, this.identifiers);
    }
    const render = html`
      ${this.isEditorMode || productCardsList.length > 0 ? html` <slot name="header-parsys"></slot> ` : nothing}
      <div class="product-cards-container">
        <div class="product-card-row-${this.orientation} mb-3">
          ${productCardsList.map(
            (item) => html`<mhecomm-k12-product-card-display
              .product="${item}"
              .programData="${this.programData}"
              cta="${this.cta}"
              showBusinessUnitLabel="${this.showBusinessUnitLabel}"
              showBusinessLabelType="${this.showProductType}"
              showCopyright="${this.showCopyright}"
              showAuthors="${this.showAuthors}"
              showProgramFamily="${this.showProgramFamily}"
              showEdition="${this.showEdition}"
              showGrades="${this.showGrades}"
              showISBN="${this.showISBN}"
              orientation="${this.orientation}"
              headingLevel="${this.headingLevel}"
            >
            </mhecomm-k12-product-card-display>`
          )}
        </div>
      </div>
      <mhecomm-product-cards-pagination
        start="${this.start}"
        total="${this.total}"
        @onPageChange=${(e) => this._search(e.detail?.page)}
      >
      </mhecomm-product-cards-pagination>
      ${this.isEditorMode || limitedResults.length > 0 ? html` <slot name="footer-parsys"></slot> ` : nothing}
    `;
    return render;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-k12-product-cards': ProductCards;
  }
}
